import { useCallback, useEffect, useRef, useState } from "react";
import { reportErrorMessage } from "./errors";
import type { Translate } from "./labels";
import type { ReportResult } from "./types";

type Runner = (page: number, signal: AbortSignal) => Promise<ReportResult>;

/**
 * Runs a report only when asked (Run report, a page change), never automatically while composing.
 * Older answers are dropped when a newer run starts.
 */
export function useReportRun(t: Translate) {
  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ranKey, setRanKey] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const run = useCallback(async (runner: Runner, page: number, key: string | null = null) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const next = await runner(page, controller.signal);
      if (controller.signal.aborted) return;
      setResult(next);
      setRanKey(key);
    } catch (caught) {
      if (controller.signal.aborted) return;
      const name = (caught as { name?: string; code?: string })?.name;
      if (name === "CanceledError" || name === "AbortError") return;
      setError(reportErrorMessage(t, caught));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [t]);

  const reset = useCallback(() => {
    controllerRef.current?.abort();
    setResult(null);
    setRanKey(null);
    setError(null);
    setLoading(false);
  }, []);

  return { result, loading, error, ranKey, run, reset };
}
